package top.wcpe.allincore.ui.runtime.host

import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.ui.runtime.animation.AnimationHandle
import top.wcpe.allincore.ui.runtime.animation.AnimationScheduler
import top.wcpe.allincore.ui.runtime.element.UIElement

/**
 * 动画宿主：M6-i 内建 `animate()` 调用所需的运行期能力集合。
 *
 * 由 IR 解释器在 session 构造完成后注入到 EvalContext，提供：
 * - 按 id 在元素树中查找目标元素（target=null 时返回 root）；
 * - 按名查找 screen 级 animation 声明，并调度播放。
 *
 * 性能：findElementById 采用 DFS，一次调用线性遍历整棵树。animate() 是事件驱动的低频调用，
 * 无需构建索引；若日后变成热点可在此处增加缓存。
 *
 * 关于调度器获取：scheduler 由 RenderRoot 在 IrSession 构造之后才挂到元素树，因此本类不持有
 * scheduler 引用，而在每次播放时从目标元素读取 [UIElement.animationScheduler]；未挂接时静默忽略。
 */
class AnimationHost(private val root: UIElement<*>?, private val animations: Map<String, UiAnimation>?) {

    /** 根元素；target 缺省时使用此元素。 */
    fun root(): UIElement<*>? = root

    /** screen 级 animation 声明表。 */
    fun animations(): Map<String, UiAnimation>? = animations

    /** 按 id 在元素树中 DFS 查找；root.id() 命中也算。找不到返回 null。 */
    fun findElementById(id: String?): UIElement<*>? {
        if (id == null || root == null) {
            return null
        }
        return findRecursive(root, id)
    }

    /**
     * 按名播放动画到指定元素。animation 名不存在或元素未挂接调度器时返回 null；
     * 调用方据此把"未找到"映射为合适的运行期错误（M6-i 在 CallDispatcher 中映射为 DslRuntimeError）。
     */
    fun play(target: UIElement<*>?, animationName: String?): AnimationHandle? {
        if (target == null || animationName == null) {
            return null
        }
        val anim = animations?.get(animationName) ?: return null
        // marker 断环，见 AnimationDriverHandle
        val scheduler = target.animationScheduler() as AnimationScheduler? ?: return null
        return scheduler.playDirect(target, anim)
    }

    private companion object {
        /** 在以 element 为根的子树中 DFS 查找 id；找不到返回 null。 */
        fun findRecursive(element: UIElement<*>, id: String): UIElement<*>? {
            if (id == element.id()) {
                return element
            }
            for (child in element.children()) {
                val hit = findRecursive(child, id)
                if (hit != null) {
                    return hit
                }
            }
            return null
        }
    }
}
